import { Stack, Typography } from '@chronosphereio/chrono-ui';
import React from 'react';
import { ExternalPanelPropsBase } from '@/core';
import { BasePanelProps } from '@/plugin-system';
import { useInterpolateContextLinkFn } from '@/plugin-system/runtime/context-links';
import { validateExternalIframeUrl } from './validate-external-iframe-url';

/**
 * Types for the external panel that are only used in the `Viz` variant.
 *
 * @see {@link ExternalPanelPropsBase} for types defined in the schema because they are also shared with
 * the `DashboardPanel` variant.
 */
interface ExternalPropsVizOnly {
  // Currently no Viz only props, but leaving this here, so it is clear where to put them in the future.
}

export interface ExternalProps extends BasePanelProps, ExternalPanelPropsBase, ExternalPropsVizOnly {
  // Extend values from `ExternalPanelPropsBase` below, as needed, to add in jsdoc to help developers.
}

const EmptyState = ({ children }: { children: React.ReactNode }) => (
  <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
    <Typography variant="body2">{children}</Typography>
  </Stack>
);

export function ExternalPanel(props: ExternalProps) {
  const { url, width, height, contentWidth, contentHeight } = props;
  const interpolateContextLink = useInterpolateContextLinkFn();

  if (width === undefined || height === undefined) return null;

  if (url === undefined || url === '') {
    return <EmptyState>No URL configured.</EmptyState>;
  }

  const validation = validateExternalIframeUrl(interpolateContextLink(url), window.location.origin);
  if (validation.validationError !== null) {
    return <EmptyState>{validation.validationError}</EmptyState>;
  }

  return (
    <iframe
      src={validation.safeUrl}
      width={contentWidth === undefined ? width : `${contentWidth}px`}
      height={contentHeight === undefined ? height : `${contentHeight}px`}
      style={{
        border: 'none',
      }}
      title="Embedded content"
      // allow-scripts: lets the iframe execute JavaScript.
      // allow-forms: lets the iframe submit forms.
      // allow-popups: lets the iframe open new windows (e.g. window.open, target="_blank").
      // allow-same-origin: lets the embedded page's own JS access its own
      //   cookies, localStorage, and same-origin XHRs. Required by
      //   authenticated third-party embeds that render their content via client-side fetches
      //   after initial load; without it those panels boot into a unique opaque origin and the
      //   in-frame data fetches fail with CORS errors.
      //
      // SECURITY: `allow-scripts` + `allow-same-origin` only enables
      // `parent.document` access when the iframe's origin equals the
      // parent's. Both paths to that are blocked above:
      //   - `javascript:`/`data:`/`vbscript:`/`file:`/`blob:` schemes
      //     (which inherit the parent's origin)
      //   - URLs that resolve to our own origin (relative paths or
      //     absolute URLs pointing back at this app)
      sandbox="allow-scripts allow-forms allow-popups allow-same-origin"
    />
  );
}
